import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  ArrowPathIcon,
  BookOpenIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { copyTextToClipboard } from "../../utils/copyTextToClipboard";
import { AIGenerator } from "../../utils/AIGenerator";
import { aiGeneratorConfigs } from "../../../config/aiGeneratorConfigs";
import { buildJobOpeningAiGuide } from "./jobOpeningAiGuide";
import {
  formatExistingOpeningsOutline,
  parseImportedJobOpeningsJson,
  SAMPLE_JOB_OPENINGS_JSON,
  serializeExistingOpeningsForAi,
  serializeJobOpeningsPayloadForExport,
} from "./jobOpeningImportUtils";
import {
  bulkImportJobOpenings,
  fetchJobOpenings,
} from "../../../api/jobOpenings";

/**
 * JSON / AI dialog for job openings (create/update + optional candidates).
 */
export function JobOpeningJsonDialog({ open, onClose, onImported }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("json");
  const [draft, setDraft] = useState("");
  const [parseError, setParseError] = useState("");
  const [importing, setImporting] = useState(false);
  const [atomic, setAtomic] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [existingOpenings, setExistingOpenings] = useState([]);
  const [loadingOpenings, setLoadingOpenings] = useState(false);
  const fileInputRef = useRef(null);
  const prevOpen = useRef(false);

  const aiGuide = useMemo(
    () => buildJobOpeningAiGuide(existingOpenings),
    [existingOpenings]
  );

  const openingsOutline = useMemo(
    () => formatExistingOpeningsOutline(existingOpenings),
    [existingOpenings]
  );

  const existingOpeningsJson = useMemo(
    () => JSON.stringify(serializeExistingOpeningsForAi(existingOpenings), null, 2),
    [existingOpenings]
  );

  useEffect(() => {
    if (!open) {
      prevOpen.current = false;
      return undefined;
    }
    if (prevOpen.current) return undefined;
    prevOpen.current = true;

    setDraft(
      serializeJobOpeningsPayloadForExport({
        atomic: false,
        openings: [
          {
            title: "",
            slug: "",
            short_description: "",
            description: "",
            responsibilities: "",
            requirement_items: [],
            requirements: "",
            benefits: "",
            location: "",
            employment_type: "full_time",
            status: "draft",
            show_on_website: false,
            applications: [],
          },
        ],
      })
    );
    setParseError("");
    setTab("json");
    setAtomic(false);
    setImportResult(null);
    setPreviewData(null);

    let cancelled = false;
    setLoadingOpenings(true);
    fetchJobOpenings({ per_page: 100, sortBy: "updated_at", sort: "DESC" })
      .then((response) => {
        if (cancelled) return;
        const rows = response?.data || [];
        setExistingOpenings(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setExistingOpenings([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingOpenings(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleCopyJson = async () => {
    try {
      await copyTextToClipboard(draft);
      toast.success(t("hr.job_openings.json.copy_success"));
    } catch {
      toast.error(t("hr.job_openings.json.clipboard_failed"));
    }
  };

  const handleCopyAiGuide = async () => {
    try {
      await copyTextToClipboard(aiGuide);
      toast.success(t("hr.job_openings.json.ai_guide_copied"));
    } catch {
      toast.error(t("hr.job_openings.json.clipboard_failed"));
    }
  };

  const handleReloadBlank = () => {
    setDraft(serializeJobOpeningsPayloadForExport({ atomic, openings: [] }));
    setParseError("");
    setPreviewData(null);
    toast.success(t("hr.job_openings.json.reload_success"));
  };

  const handleInsertSample = () => {
    setDraft(SAMPLE_JOB_OPENINGS_JSON);
    setParseError("");
    setPreviewData(null);
  };

  const handleUploadFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setDraft(String(reader.result || ""));
      setParseError("");
      setPreviewData(null);
      toast.success(t("hr.job_openings.json.file_loaded"));
    };
    reader.onerror = () => toast.error(t("hr.job_openings.json.file_failed"));
    reader.readAsText(file);
  };

  const runImport = async (payload) => {
    setImporting(true);
    setImportResult(null);
    try {
      const result = await bulkImportJobOpenings(payload);
      const counts = result?.counts || {};
      toast.success(
        result?.message ||
          t("hr.job_openings.json.imported", {
            created: counts.openings_created || 0,
            updated: counts.openings_updated || 0,
            applications: (counts.applications_created || 0) + (counts.applications_updated || 0),
          })
      );
      if (Array.isArray(result?.errors) && result.errors.length) {
        toast.error(result.errors.slice(0, 3).join(" · "));
      }
      if (Array.isArray(result?.warnings) && result.warnings.length) {
        toast(result.warnings.slice(0, 2).join(" · "), { icon: "⚠️" });
      }
      setImportResult(result);
      onImported?.(result);
      try {
        const response = await fetchJobOpenings({
          per_page: 100,
          sortBy: "updated_at",
          sort: "DESC",
        });
        const rows = response?.data || [];
        setExistingOpenings(Array.isArray(rows) ? rows : []);
      } catch {
        // keep previous inventory
      }
    } catch (e) {
      const apiErrors = e?.response?.data?.errors;
      const message =
        (Array.isArray(apiErrors) && apiErrors.join(", ")) ||
        e?.response?.data?.message ||
        e?.message ||
        t("hr.job_openings.json.import_failed");
      setParseError(message);
      toast.error(message);
    } finally {
      setImporting(false);
    }
  };

  const handlePreview = () => {
    try {
      const parsed = parseImportedJobOpeningsJson(draft);
      if (parsed.droppedCount > 0) {
        toast.error(
          t("hr.job_openings.json.dropped_rows", {
            count: parsed.droppedCount,
            detail: (parsed.dropReasons || []).slice(0, 5).join(", "),
          })
        );
      }
      setParseError("");
      setPreviewData({ ...parsed, atomic: atomic || parsed.atomic });
    } catch (e) {
      const code = e?.code || "unknown";
      const key = `hr.job_openings.json.error_${code}`;
      const msg = t(key);
      const display = msg !== key ? msg : e?.message || String(e);
      setPreviewData(null);
      setParseError(display);
      toast.error(display);
    }
  };

  const handleConfirmImport = async () => {
    if (!previewData) return;
    await runImport(previewData);
    setPreviewData(null);
  };

  const createdOpenings = [
    ...(importResult?.created?.openings || []),
    ...(importResult?.updated?.openings || []),
  ];

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      size="4xl"
      title={t("hr.job_openings.json.dialog_title")}
      zIndex={4000}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
          <button
            type="button"
            onClick={() => setTab("json")}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
              tab === "json" ? "bg-sky-100 text-sky-900" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            <DocumentTextIcon className="h-5 w-5 shrink-0" />
            {t("hr.job_openings.json.tab_json")}
          </button>
          <button
            type="button"
            onClick={() => setTab("ai")}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
              tab === "ai" ? "bg-slate-100 text-slate-900" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            <BookOpenIcon className="h-5 w-5 shrink-0" />
            {t("hr.job_openings.json.tab_ai_guide")}
          </button>
        </div>

        {tab === "json" ? (
          <>
            <p className="text-sm text-gray-600">{t("hr.job_openings.json.hint")}</p>
            <details className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700" open>
              <summary className="cursor-pointer font-medium">
                {t("hr.job_openings.json.existing_openings")}
                {loadingOpenings
                  ? ` (${t("hr.job_openings.json.loading")})`
                  : ` (${existingOpenings.length})`}
              </summary>
              <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap font-mono">
                {openingsOutline}
              </pre>
            </details>
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={atomic}
                onChange={(e) => setAtomic(e.target.checked)}
              />
              {t("hr.job_openings.json.atomic")}
            </label>
            <textarea
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setParseError("");
                setPreviewData(null);
              }}
              spellCheck={false}
              className="min-h-[min(360px,45vh)] w-full flex-1 rounded-lg border border-gray-300 font-mono text-xs leading-relaxed focus:border-sky-500 focus:ring-2 focus:ring-sky-500"
            />
            {parseError ? (
              <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-800">{parseError}</p>
            ) : null}
            {previewData ? (
              <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-3 text-sm text-sky-950">
                <p className="font-medium">
                  {t("hr.job_openings.json.preview_title", {
                    count: previewData.openings.length,
                  })}
                </p>
                <ul className="mt-2 space-y-1 text-sm">
                  {previewData.openings.map((opening, idx) => (
                    <li key={opening.slug || `${opening.title}-${idx}`}>
                      <span className="font-medium">{opening.title}</span>
                      {opening.slug ? (
                        <span className="ml-2 text-xs text-sky-800">{opening.slug}</span>
                      ) : null}
                      <span className="ml-2 text-xs text-sky-800">
                        {opening.status || "draft"}
                      </span>
                      {Array.isArray(opening.applications) && opening.applications.length ? (
                        <span className="ml-2 text-xs text-sky-800">
                          +{opening.applications.length}{" "}
                          {t("hr.job_openings.json.preview_applications")}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-sky-900">
                  {t("hr.job_openings.json.preview_hint")}
                </p>
              </div>
            ) : null}
            {importResult ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-950">
                <p className="font-medium">{t("hr.job_openings.json.result_title")}</p>
                <p className="mt-1 text-xs text-emerald-900">
                  {t("hr.job_openings.json.result_hint")}
                </p>
                {createdOpenings.length > 0 ? (
                  <ul className="mt-2 space-y-1 text-sm">
                    {createdOpenings.map((opening) => (
                      <li key={opening.id}>
                        <Link
                          className="text-sky-800 underline hover:text-sky-950"
                          to={`/dashboard/hr/job-openings/${opening.id}`}
                          onClick={onClose}
                        >
                          {opening.title || `#${opening.id}`}
                        </Link>
                        {opening.slug ? (
                          <span className="ml-2 text-xs text-emerald-800">{opening.slug}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-xs">{t("hr.job_openings.json.result_none")}</p>
                )}
                {Array.isArray(importResult.errors) && importResult.errors.length ? (
                  <ul className="mt-2 list-disc pl-5 text-xs text-red-800">
                    {importResult.errors.slice(0, 8).map((err) => (
                      <li key={err}>{err}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleCopyJson}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ClipboardDocumentIcon className="h-5 w-5" />
                {t("hr.job_openings.json.copy_json")}
              </button>
              <button
                type="button"
                onClick={handleReloadBlank}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ArrowPathIcon className="h-5 w-5" />
                {t("hr.job_openings.json.reload_blank")}
              </button>
              <button
                type="button"
                onClick={handleInsertSample}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t("hr.job_openings.json.insert_sample")}
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t("hr.job_openings.json.upload")}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json,.json,text/plain"
                className="hidden"
                onChange={handleUploadFile}
              />
              <AIGenerator
                module="job_opening"
                config={aiGeneratorConfigs.job_opening}
                context={{
                  title: "",
                  description: "",
                  existing_openings: existingOpeningsJson,
                }}
                fieldsToGenerate={["openings_json"]}
                onGenerate={(generatedFields) => {
                  const raw = generatedFields?.openings_json;
                  if (!raw) {
                    toast.error(t("hr.job_openings.json.ai_parse_error"));
                    return;
                  }
                  try {
                    const parsed = parseImportedJobOpeningsJson(
                      typeof raw === "string" ? raw : JSON.stringify(raw)
                    );
                    if (parsed.droppedCount > 0) {
                      toast.error(
                        t("hr.job_openings.json.dropped_rows", {
                          count: parsed.droppedCount,
                          detail: (parsed.dropReasons || []).slice(0, 5).join(", "),
                        })
                      );
                    }
                    setDraft(serializeJobOpeningsPayloadForExport(parsed));
                    setAtomic(Boolean(parsed.atomic));
                    setParseError("");
                    setPreviewData(null);
                    toast.success(
                      t("hr.job_openings.json.ai_apply_success", {
                        count: parsed.openings.length,
                      })
                    );
                    setTab("json");
                  } catch {
                    toast.error(t("hr.job_openings.json.ai_parse_error"));
                  }
                }}
                buttonLabel={t("hr.job_openings.json.generate_with_ai")}
              />
              {previewData ? (
                <button
                  type="button"
                  onClick={handleConfirmImport}
                  disabled={importing}
                  className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
                >
                  {importing
                    ? t("hr.job_openings.json.importing")
                    : t("hr.job_openings.json.confirm_import", {
                        count: previewData.openings.length,
                      })}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handlePreview}
                  disabled={importing}
                  className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
                >
                  {t("hr.job_openings.json.preview")}
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600">
              {t("hr.job_openings.json.ai_guide_intro")}
            </p>
            <pre className="max-h-[min(420px,55vh)] overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-4 text-left font-mono text-xs leading-relaxed text-slate-800">
              {aiGuide}
            </pre>
            <button
              type="button"
              onClick={handleCopyAiGuide}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
              {t("hr.job_openings.json.copy_ai_guide")}
            </button>
          </>
        )}
      </div>
    </PMDialog>
  );
}
